"use client";
import {JSX, ReactNode, useState} from "react";

import {ButtonSimple} from "@/shared/ui/buttons";
import {Typography} from "@/shared/ui/data-display/typography";

import "./styles/links-group.scss";

interface SubLink {
    label: string;
    href: string;
}

export interface LinkItem {
    label: string;
    href?: string;
    sublinks?: Array<SubLink>;
    customElement?: ReactNode;
}

interface LinksGroupProps {
    title: string;
    links: Array<LinkItem>;
    className?: string;
}

export function LinksGroup({title, links, className = ""}: LinksGroupProps): JSX.Element {
    const [openSublinks, setOpenSublinks] = useState<Set<number>>(new Set());

    const toggleSublinks = (index: number): void => {
        setOpenSublinks((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(index)) {
                newSet.delete(index);
            } else {
                newSet.add(index);
            }
            return newSet;
        });
    };

    return (
        <div className={`links-group ${className}`}>
            <Typography as="h3" className="links-group__title" options={["uppercase"]}>
                {title}
            </Typography>

            <ul className="links-group__list">
                {links.map((link, index) => (
                    <li key={index} className="links-group__item">
                        {link.customElement ? (
                            link.customElement
                        ) : link.sublinks ? (
                            <>
                                <ButtonSimple
                                    as="button"
                                    size="sm"
                                    variant="secondary"
                                    className="links-group__toggle"
                                    onClick={() => toggleSublinks(index)}
                                    aria-expanded={openSublinks.has(index)}
                                >
                                    {link.label}
                                    <span className={`links-group__toggle-icon ${openSublinks.has(index) ? "links-group__toggle-icon--open" : ""}`} />
                                </ButtonSimple>
                                {openSublinks.has(index) && (
                                    <ul className="links-group__sublist">
                                        {link.sublinks.map((sublink, subIndex) => (
                                            <li key={subIndex}>
                                                <ButtonSimple as="link" href={sublink.href} size="sm" variant="secondary">
                                                    {sublink.label}
                                                </ButtonSimple>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </>
                        ) : (
                            link.href && (
                                <ButtonSimple as="link" href={link.href} size="sm" variant="secondary">
                                    {link.label}
                                </ButtonSimple>
                            )
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
}
